import React, { useRef, useState, useEffect } from "react";
import BaseURL from "../../../utils/base-uri.json";
import axios from "../../../utils/fvAxios";
import LoadingSpinner from "../../../toolkit/LoadingSpinner";
import ErrorAlert from "../../../toolkit/ErrorAlert";
import { Box } from "@mui/material";
import Message from "./Message/Message";
import Delayed from "../../../utils/Delayed";
import "./../../Widget/Widget.css";
import ListSkeleton from "./ListSkeleton";
import { messageContextOptions } from "../ContextMenuHelper";

const MESSAGE_LIMIT = 10; // number of messages to fetch at a time
let page = 1;
let isLoading = false;
let error = false;
let hasMore = true;
let totalPages = 1;
const queryUrl = BaseURL.baseUrl + BaseURL.widget.GetCommunicationDetails;
const pinnedUrl = BaseURL.baseUrl + BaseURL.widget.GetPinnedMessages;

function MessageList({
  pqId,
  newMessageData,
  showPinned,
  onSaveUpdatedMessage,
  unPinnedAll,
  onDeleteMessage,
  onPinChanges,
  afterMessageDelete,
  listHeight
}) {
  let options = messageContextOptions;
  if (showPinned) options = options.filter(({ name }) => name !== "Edit");
  const [communications, setCommunications] = useState([]);
  const [readyToRender, setReadyToRender] = useState(false);
  const listContainerRef = useRef(null);
  const lastMessageRef = useRef(null);
  const resetParams = () => {
    totalPages = 1;
    hasMore = true;
    page = 1;
    isLoading = false;
  };
  useEffect(() => {
    setCommunications([]);
    resetParams();
    fetchMessages();
  }, [showPinned, unPinnedAll]);

  useEffect(() => { }, [onSaveUpdatedMessage]);

  useEffect(() => {
    updateCommunications(newMessageData);
  }, [newMessageData]);

  const onPinOrFlagChanges = (dataToUpdate, removePinned) => {
    if (showPinned && removePinned)
      setCommunications((prevCommunications) =>
        prevCommunications.filter(
          (communication) => communication.messageId !== dataToUpdate.messageId
        )
      );
    else updateCommunications(dataToUpdate);
  };

  const updateCommunications = (dataToUpdate) => {
    if (dataToUpdate && dataToUpdate.isEdited && !dataToUpdate.isDeleted) {
      //updation
      setCommunications((prevCommunications) => {
        const updatedCommunications = prevCommunications.map(
          (communication) => {
            if (communication.messageId === dataToUpdate.messageId) {
              if (
                dataToUpdate.isPinned !== undefined && dataToUpdate.isFlagged !== undefined
              ) {
                return {
                  ...communication,
                  message: dataToUpdate.message,
                  isPinned: dataToUpdate.isPinned,
                  isFlagged: dataToUpdate.isFlagged,
                  accessType: dataToUpdate.accessType,
                }
              } else {
                return {
                  ...communication,
                  message: dataToUpdate.message,
                  accessType: dataToUpdate.accessType,
                };
              }
            }
            return communication;
          }
        );
        return updatedCommunications;
      });
    } else if (
      dataToUpdate &&
      !dataToUpdate.isDeleted &&
      dataToUpdate?.messageId > 0
    ) {
      //addition
      setCommunications((prevComm) => {
        return [...prevComm, dataToUpdate];
      });
      setTimeout(() => {
        listContainerRef.current.scrollTo({
          top: listContainerRef.current.scrollHeight,
          behavior: "smooth",
        });
      }, 200);
    } else if (
      dataToUpdate &&
      dataToUpdate.isDeleted &&
      dataToUpdate?.messageId > 0
    ) {
      //deletion
      setCommunications((prevCommunications) =>
        prevCommunications.filter(
          (communication) => communication.messageId !== dataToUpdate.messageId
        )
      );
      afterMessageDelete(true);
    }
  };

  const fetchMessages = async () => {
    if (isLoading || !hasMore) return;
    isLoading = true;
    error = false;
    try {
      setReadyToRender(false);
      const response = await axios.get(
        (showPinned ? pinnedUrl : queryUrl) +
        "?prequalificationId=" +
        pqId +
        "&pageSize=" +
        MESSAGE_LIMIT +
        "&pageNumber=" +
        page
      );
      if (response.data?.responseCode === 100) {
        let prevHeight = listContainerRef?.current?.scrollHeight ?? 0;
        if (response.data.data) {
          setCommunications((prevCommunications) => {
            // remove any duplicates from the new communications before adding them to the existing array
            const newCommunications = response.data.data.filter(
              (communication) =>
                !prevCommunications.some(
                  (prevComm) => prevComm.messageId === communication.messageId
                )
            );
            totalPages = Math.ceil(
              response.data?.data[0]?.totalMessageCount / MESSAGE_LIMIT
            );
            return [...newCommunications.reverse(), ...prevCommunications];
          });
          if (response.data?.data?.length === 0) hasMore = false;
          isLoading = false;

          setTimeout(() => {
            if (page === 1) prevHeight = 0;
            try {
              listContainerRef.current.scrollTop =
                listContainerRef.current.scrollHeight - prevHeight;
              setReadyToRender(true);
            } catch (e) { }
          }, 200);
        }
      } else error = true;
    } catch (e) {
      console.error(e);
      error = true;
      isLoading = false;
    }
  };

  const handleScroll = () => {
    const container = listContainerRef.current;
    const scrolledToTop = container.scrollTop === 0; // check if user has scrolled to the top

    if (scrolledToTop) {
      if (!isLoading) {
        if (totalPages > page) {
          page = page + 1;
          fetchMessages();
        }
      }
    }
  };

  // attach scroll event listener when component mounts
  useEffect(() => {
    const container = listContainerRef.current;
    container.addEventListener("scroll", handleScroll);
    return () => {
      isLoading = true;
      container.removeEventListener("scroll", handleScroll);
    };
  }, []);
  const messageUpdateHandler = (messageData) => {
    onSaveUpdatedMessage(messageData);
  };
  const deleteMessageHandler = (messageData) => {
    onDeleteMessage(messageData);
  };

  return (
    <>
      <Box
        ref={listContainerRef}
        px={2}
        //style={{ height: `${listHeight}`, overflowY: "auto" }}
        style={{
          flex: "1 1 auto",
          overflowY: "auto",
          minHeight: 0
        }}
       className={`list ${readyToRender ? "high-visibility" : "low-visibility"}`}
      >
        {error && <ErrorAlert message={"Error fetching information"} />}
        {isLoading && (
          <div className="list-item-loader">
            <LoadingSpinner />
          </div>
        )}
        {!isLoading && communications.length === 0 && (
          <Delayed waitBeforeShow={2000}>
            <ErrorAlert severity="warning" message={"No results found!"} />
          </Delayed>
        )}
        {communications.length === 0 && isLoading ? (
          <ListSkeleton />
        ) : (
          communications.map((i, idx) => (
            <div className="list-item border-0 px-0" key={idx}>
              <Message
                {...i}
                options={options}
                onPinOrFlagChanges={(data, args) => {
                  onPinChanges(data);
                  onPinOrFlagChanges(data, args);
                }}
                onMessageUpdate={messageUpdateHandler}
                onDeleteMessage={deleteMessageHandler}
              />
            </div>
          ))
        )}
        <div ref={lastMessageRef}></div>
      </Box >
    </>
  );
}

export default MessageList;
